/**
 * ParticipationHistory — User's personal record of real-world participation.
 *
 * Archival, quiet, personal. Not gamified, not performative.
 * Feels like: record, continuity, lived participation.
 * Not: streaks, achievements, points, badges, rankings, social comparison.
 *
 * Default visibility: private.
 * Options: private, circle, contacts, public (controlled by user).
 */

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, MapPin, Users, Landmark, Building,
  ChevronRight, Eye, EyeOff, Shield, Globe, UserCheck, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_STYLES = {
  organized: { label: 'Organized', color: 'var(--lk-sage)', bg: 'rgba(122,143,91,0.12)' },
  attended:  { label: 'Attended',  color: '#7FA5B8', bg: 'rgba(127,165,184,0.12)' },
  helped:    { label: 'Helped',    color: '#C2A45E', bg: 'rgba(194,164,94,0.12)' },
};

const VISIBILITY_ICONS = {
  private: Lock,
  circle: Users,
  contacts: UserCheck,
  public: Globe,
};

const VISIBILITY_LABELS = {
  private: 'Private',
  circle: 'Circle',
  contacts: 'Contacts',
  public: 'Public',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function groupByMonth(records) {
  const groups = {};
  for (const r of records) {
    const dateStr = r.date || r.start_time || '';
    if (!dateStr) continue;
    const d = new Date(dateStr);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    if (!groups[key]) groups[key] = { key, label, records: [] };
    groups[key].records.push(r);
  }
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
}

export default function ParticipationHistory() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [visibility, setVisibility] = useState('private');
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [showVisSettings, setShowVisSettings] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user, roleFilter]);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [historyRes, summaryRes, visRes] = await Promise.all([
        fetch(`${API}/api/participation/mine?role=${roleFilter}&limit=100`, { headers }),
        fetch(`${API}/api/participation/summary`, { headers }),
        fetch(`${API}/api/participation/visibility`, { headers }),
      ]);

      if (historyRes.ok) {
        const hData = await historyRes.json();
        setRecords(hData.records || []);
      }
      if (summaryRes.ok) {
        const sData = await summaryRes.json();
        setSummary(sData);
      }
      if (visRes.ok) {
        const vData = await visRes.json();
        setVisibility(vData.visibility || 'private');
      }
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  const updateVisibility = async (newVis) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/participation/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ visibility: newVis }),
      });
      if (res.ok) {
        setVisibility(newVis);
        toast.success(`Visibility set to ${VISIBILITY_LABELS[newVis]}`);
        setShowVisSettings(false);
      }
    } catch {
      toast.error('Failed to update visibility');
    }
  };

  const months = groupByMonth(records);

  if (!user) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-surface-root)' }}>
        <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
          <div className="max-w-3xl mx-auto px-4 flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <span className="text-lg font-bold ml-2" style={{ color: 'var(--fy-text-primary)' }}>Participation</span>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p style={{ color: 'var(--fy-text-secondary)' }}>Sign in to view your participation history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-surface-root)' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}
      >
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:opacity-70"
              data-testid="participation-back-btn"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
                Participation
              </h1>
              <p className="text-[10px] tracking-widest uppercase" style={{ color: 'var(--fy-text-disabled)' }}>
                Personal Record
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowVisSettings(!showVisSettings)}
            className="p-2 rounded-full hover:opacity-70"
            data-testid="visibility-settings-btn"
          >
            {React.createElement(VISIBILITY_ICONS[visibility] || Lock, {
              className: 'w-4 h-4',
              style: { color: 'var(--fy-text-secondary)' },
            })}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-5">
        {/* Visibility settings dropdown */}
        {showVisSettings && (
          <Card
            className="p-4"
            style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="visibility-settings-panel"
          >
            <p className="text-xs font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Who can see your participation history?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {['private', 'circle', 'contacts', 'public'].map(opt => {
                const Icon = VISIBILITY_ICONS[opt];
                const isActive = visibility === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => updateVisibility(opt)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all"
                    style={{
                      backgroundColor: isActive ? 'rgba(122,143,91,0.15)' : 'var(--fy-surface-muted)',
                      color: isActive ? 'var(--lk-sage)' : 'var(--fy-text-secondary)',
                      border: isActive ? '1px solid var(--lk-sage)' : '1px solid transparent',
                    }}
                    data-testid={`visibility-${opt}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {VISIBILITY_LABELS[opt]}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] mt-2" style={{ color: 'var(--fy-text-disabled)' }}>
              {visibility === 'private' && 'Only you can see your participation history.'}
              {visibility === 'circle' && 'Contacts and shared group members can view.'}
              {visibility === 'contacts' && 'Only your contacts can view.'}
              {visibility === 'public' && 'Anyone can view your participation history.'}
            </p>
          </Card>
        )}

        {/* Summary cards */}
        {summary && !loading && (
          <div className="grid grid-cols-3 gap-3" data-testid="participation-summary">
            <SummaryCard
              label="Organized"
              total={summary.organized?.total || 0}
              completed={summary.organized?.completed || 0}
              color="var(--lk-sage)"
            />
            <SummaryCard
              label="Attended"
              total={summary.attended?.total || 0}
              completed={summary.attended?.completed || 0}
              color="#7FA5B8"
            />
            <SummaryCard
              label="Helped"
              total={summary.helped?.total || 0}
              color="#C2A45E"
            />
          </div>
        )}

        {/* Associations */}
        {summary && (summary.associations?.groups > 0 || summary.associations?.commons > 0 || summary.associations?.institutions > 0) && (
          <div className="flex flex-wrap gap-2" data-testid="participation-associations">
            {summary.associations.groups > 0 && (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                <Users className="w-3 h-3" /> {summary.associations.groups} group{summary.associations.groups !== 1 ? 's' : ''}
              </span>
            )}
            {summary.associations.commons > 0 && (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                <Landmark className="w-3 h-3" /> {summary.associations.commons} commons
              </span>
            )}
            {summary.associations.institutions > 0 && (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                <Building className="w-3 h-3" /> {summary.associations.institutions} institution{summary.associations.institutions !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2" data-testid="participation-filters">
          {[
            { id: 'all', label: 'All' },
            { id: 'organized', label: 'Organized' },
            { id: 'attended', label: 'Attended' },
            { id: 'helped', label: 'Helped' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setRoleFilter(tab.id)}
              className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: roleFilter === tab.id ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                color: roleFilter === tab.id ? '#fff' : 'var(--fy-text-secondary)',
              }}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16" data-testid="participation-empty">
            <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--fy-border-subtle)' }} />
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              No participation records yet.
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
              Records appear as you organize, attend, or help with activities.
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="participation-timeline">
            {months.map(month => (
              <div key={month.key}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--fy-text-disabled)' }}>
                    {month.label}
                  </span>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'var(--fy-border-subtle)' }} />
                  <span className="text-[10px]" style={{ color: 'var(--fy-text-disabled)' }}>
                    {month.records.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {month.records.map(record => (
                    <RecordCard
                      key={`${record.activity_id}-${record.role}`}
                      record={record}
                      onOpen={() => navigate(`/activity/${record.activity_id}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ label, total, completed, color }) {
  return (
    <Card
      className="p-3 text-center"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
    >
      <p className="text-2xl font-bold" style={{ color }} data-testid={`summary-${label.toLowerCase()}-count`}>
        {total}
      </p>
      <p className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: 'var(--fy-text-secondary)' }}>
        {label}
      </p>
      {completed !== undefined && completed > 0 && (
        <p className="text-[10px] mt-0.5" style={{ color: 'var(--fy-text-disabled)' }}>
          {completed} completed
        </p>
      )}
    </Card>
  );
}

function RecordCard({ record, onOpen }) {
  const role = ROLE_STYLES[record.role] || ROLE_STYLES.attended;

  return (
    <Card
      className="overflow-hidden cursor-pointer transition-all hover:opacity-90"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      onClick={onOpen}
      data-testid={`record-${record.activity_id}`}
    >
      <div className="p-3 flex items-center gap-3">
        {/* Role indicator */}
        <div
          className="w-1.5 self-stretch rounded-full flex-shrink-0"
          style={{ backgroundColor: role.color }}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: role.bg, color: role.color }}
            >
              {record.role_label}
            </span>
            {record.is_completed && (
              <span className="text-[10px]" style={{ color: 'var(--lk-sage)' }}>Completed</span>
            )}
          </div>
          <h3 className="text-sm font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>
            {record.title}
          </h3>
          <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[11px]" style={{ color: 'var(--fy-text-disabled)' }}>
            {record.date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(record.date)}
              </span>
            )}
            {record.location && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                {record.location}
              </span>
            )}
          </div>
        </div>

        <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
      </div>
    </Card>
  );
}
